import React, {useId, useRef, useState} from "react";
import Modal from "./Modal";
import {C} from "../styles";
import {apiAdminLandingAiSectionDraft} from "../api/landing";
import BLOCK_FIELDS from "../landing/blockFields.json";

const BRIEF_MIN = 3;
const BRIEF_MAX = 1500;

const smallButton = {border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 13};
const primaryButton = (disabled) => ({
    ...smallButton, background: disabled ? "#ccc" : C.primary, color: "#fff", border: "none", cursor: disabled ? "not-allowed" : "pointer",
});

// Contenu d'un bloc proposé en texte brut : un texte par champ, ou une ligne par élément d'une liste.
function textLines(data) {
    const lines = [];
    for (const value of Object.values(data || {})) {
        if (typeof value === "string" && value !== "") lines.push(value);
        else if (Array.isArray(value)) {
            for (const item of value) {
                const line = Object.values(item || {}).filter(v => typeof v === "string" && v !== "").join(" — ");
                if (line) lines.push(line);
            }
        }
    }
    return lines;
}

// Bouton « ✨ Proposer une rubrique » + fenêtre : l'IA propose une suite de blocs que l'admin prévisualise. Rien n'est ajouté
// (onAdd) qu'au clic sur « Ajouter ces blocs », et jamais enregistré ici. Le texte de l'IA est affiché comme texte (jamais du HTML).
export default function LandingAiSectionDraft({disabled = false, remainingBlocks, onAdd}) {
    const id = useId();
    const busy = useRef(false);
    const [open, setOpen] = useState(false);
    const [brief, setBrief] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null); // {name, blocks, note}
    const [reuseName, setReuseName] = useState(false);

    const briefLength = brief.trim().length;
    const canGenerate = briefLength >= BRIEF_MIN && briefLength <= BRIEF_MAX && !loading;
    const kept = result ? result.blocks.slice(0, Math.max(0, remainingBlocks)) : [];
    const dropped = result ? result.blocks.length - kept.length : 0;

    function close() {
        setOpen(false);
        setBrief("");
        setError("");
        setResult(null);
        setReuseName(false);
    }

    async function generate() {
        if (busy.current || !canGenerate) return;
        busy.current = true; // verrou synchrone : un double clic n'envoie qu'une requête
        setLoading(true);
        setError("");
        setResult(null);
        try {
            const res = await apiAdminLandingAiSectionDraft({brief: brief.trim()});
            if (!res || res.error) {
                setError((res && (res.message || res.error)) || "L'assistant n'a pas pu répondre.");
                return;
            }
            const blocks = (Array.isArray(res.blocks) ? res.blocks : []).filter(b => b && BLOCK_FIELDS.blocks[b.type]);
            if (blocks.length === 0) {
                setError("L'assistant n'a rien proposé. Reformulez votre objectif.");
                return;
            }
            setResult({name: typeof res.name === "string" ? res.name : "", blocks, note: typeof res.note === "string" ? res.note : ""});
        } finally {
            busy.current = false;
            setLoading(false);
        }
    }

    function add() {
        const proposal = {name: reuseName && result.name ? result.name : "", blocks: kept.map(b => ({type: b.type, data: b.data || {}}))};
        close();
        onAdd(proposal);
    }

    return (
        <div style={{display: "inline-block"}}>
            <button type="button" style={smallButton} disabled={disabled} aria-describedby={disabled ? `${id}-off` : undefined}
                    onClick={() => setOpen(true)}>✨ Proposer une rubrique</button>
            {disabled && (
                <span id={`${id}-off`} style={{marginLeft: 8, fontSize: 12, color: C.textMuted}}>
                    L'assistant IA n'est pas disponible sur ce serveur.
                </span>
            )}
            {open && (
                <Modal title="Proposer une rubrique" onClose={close} scrollable>
                    <p style={{marginTop: 0, fontSize: 13, color: C.textMuted}}>
                        L'assistant propose des blocs, que vous prévisualisez avant de les ajouter à la fin de la rubrique. Rien n'est enregistré tant que vous ne cliquez pas sur « Enregistrer ».
                    </p>
                    <label htmlFor={`${id}-brief`} style={{display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4}}>
                        Quel est l'objectif de cette rubrique ?
                    </label>
                    <textarea id={`${id}-brief`} value={brief} maxLength={BRIEF_MAX} rows={4} disabled={loading}
                              onChange={e => setBrief(e.target.value)}
                              style={{width: "100%", boxSizing: "border-box", padding: "7px 9px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 14, fontFamily: "inherit"}}/>
                    <div style={{fontSize: 12, color: C.textMuted, textAlign: "right"}}>{brief.length} / {BRIEF_MAX}</div>
                    <div style={{display: "flex", justifyContent: "flex-end", margin: "8px 0"}}>
                        <button type="button" style={primaryButton(!canGenerate)} disabled={!canGenerate} onClick={generate}>
                            {loading ? "Génération en cours…" : "Générer"}
                        </button>
                    </div>
                    {error && <p role="alert" style={{color: C.danger, fontSize: 13, margin: "8px 0"}}>{error}</p>}

                    {result && (
                        <div>
                            <h4 style={{margin: "12px 0 6px"}}>Proposition</h4>
                            {result.note && <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 8px", whiteSpace: "pre-wrap"}}>Note de l'assistant : {result.note}</p>}
                            {result.name && (
                                <label style={{display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 8}}>
                                    <input type="checkbox" checked={reuseName} onChange={e => setReuseName(e.target.checked)}/>
                                    Reprendre le nom proposé : « {result.name} »
                                </label>
                            )}
                            {result.blocks.map((b, i) => (
                                <div key={i} style={{border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginBottom: 8, opacity: i < kept.length ? 1 : 0.5}}>
                                    <div style={{fontSize: 12, color: C.textMuted}}>{BLOCK_FIELDS.blocks[b.type].label}</div>
                                    {textLines(b.data).map((line, j) => <div key={j} style={{whiteSpace: "pre-wrap", fontSize: 14}}>{line}</div>)}
                                </div>
                            ))}
                            {dropped > 0 && (
                                <p role="status" style={{fontSize: 13, margin: "0 0 8px"}}>
                                    {dropped} bloc{dropped > 1 ? "s" : ""} non ajouté{dropped > 1 ? "s" : ""} : limite de {BLOCK_FIELDS.maxBlocksPerSection} blocs par rubrique.
                                </p>
                            )}
                        </div>
                    )}

                    <div style={{display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12}}>
                        <button type="button" style={smallButton} onClick={close}>Annuler</button>
                        {result && (
                            <button type="button" style={primaryButton(kept.length === 0)} disabled={kept.length === 0} onClick={add}>
                                Ajouter ces blocs
                            </button>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}
